import React, { useState, useEffect } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

/**
 * W-9 capture: upload a PDF, review what Gemini read, confirm.
 *
 * Two gl-api calls, never collapsed into one: /extract reads the PDF and
 * proposes fields but writes nothing. /confirm is the only call that
 * certifies a W-9 -- it sends back whatever is in this form, which may have
 * been hand-corrected. This modal's two steps exist to make that gate
 * visible: step 1 is a proposal, step 2 is a decision.
 */

const ENTITY_TYPES = [
  { value: '1', key: 'w9.entity.individual', label: 'Individual / Sole Proprietor' },
  { value: '2', key: 'w9.entity.c_corp', label: 'C Corporation' },
  { value: '3', key: 'w9.entity.s_corp', label: 'S Corporation' },
  { value: '4', key: 'w9.entity.partnership', label: 'Partnership' },
  { value: '5', key: 'w9.entity.trust_estate', label: 'Trust / Estate' },
  { value: '6', key: 'w9.entity.llc', label: 'Limited Liability Company (LLC)' },
  { value: '7', key: 'w9.entity.other', label: 'Other' },
];

const LLC_ENTITY_TYPE = '6';
const LLC_CLASSES = ['C', 'S', 'P'];

interface ReviewValues {
  legal_name: string;
  business_name_dba: string;
  entity_type: string;
  llc_tax_class: string;
  tin_type: string;
  tin: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  postal_code: string;
  account_number: string;
  exempt_payee_code: string;
  fatca_code: string;
  has_foreign_partners: boolean;
  signer_name: string;
  signer_title: string;
  signature_date: string;
  w9_revision: string;
}

const EMPTY_REVIEW: ReviewValues = {
  legal_name: '',
  business_name_dba: '',
  entity_type: '1',
  llc_tax_class: '',
  tin_type: '1',
  tin: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  postal_code: '',
  account_number: '',
  exempt_payee_code: '',
  fatca_code: '',
  has_foreign_partners: false,
  signer_name: '',
  signer_title: '',
  signature_date: '',
  w9_revision: '',
};

const toReview = (extracted: any): ReviewValues => ({
  legal_name: extracted.legal_name || '',
  business_name_dba: extracted.business_name_dba || '',
  entity_type: String(extracted.entity_type ?? '1'),
  llc_tax_class: extracted.llc_tax_class || '',
  tin_type: String(extracted.tin_type ?? '1'),
  tin: extracted.tin || '',
  address_line1: extracted.address_line1 || '',
  address_line2: extracted.address_line2 || '',
  city: extracted.city || '',
  state: extracted.state || '',
  postal_code: extracted.postal_code || '',
  account_number: extracted.account_number || '',
  exempt_payee_code: extracted.exempt_payee_code || '',
  fatca_code: extracted.fatca_code || '',
  has_foreign_partners: Boolean(extracted.has_foreign_partners),
  signer_name: extracted.signer_name || '',
  signer_title: extracted.signer_title || '',
  signature_date: extracted.signature_date || '',
  w9_revision: extracted.w9_revision || '',
});

interface Props {
  show: boolean;
  vendorId: number;
  vendorName: string;
  branchId: number;
  onClose: () => void;
  extractW9: (data: any, onSuccess: Function, onError: Function) => void;
  confirmW9: (data: any, onSuccess: Function, onError: Function) => void;
  fetchCurrentW9?: (data: any, onSuccess: Function, onError: Function) => void;
  downloadW9Pdf?: (data: any, onSuccess: Function, onError: Function) => void;
  onConfirmed: () => void;
}

const W9Modal: React.FC<Props> = ({
  show, vendorId, vendorName, branchId, onClose, extractW9, confirmW9,
  fetchCurrentW9, downloadW9Pdf, onConfirmed,
}) => {
  const { t } = useTranslation('Tax1099Vendors');

  const [step, setStep] = useState<'upload' | 'review'>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [fileId, setFileId] = useState<number | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [values, setValues] = useState<ReviewValues>(EMPTY_REVIEW);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [currentW9, setCurrentW9] = useState<any>(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    if (show && vendorId && fetchCurrentW9) {
      fetchCurrentW9(
        { vendor_id: vendorId, branch_id: branchId },
        (_message: string, response: any) => {
          setCurrentW9(response?.data?.data ?? null);
        },
        () => {
          setCurrentW9(null);
        },
      );
    } else {
      setCurrentW9(null);
    }
  }, [show, vendorId, branchId, fetchCurrentW9]);

  const reset = () => {
    setStep('upload');
    setFile(null);
    setFileId(null);
    setWarnings([]);
    setValues(EMPTY_REVIEW);
    setErrors({});
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const setField = (field: keyof ReviewValues, value: any) => {
    setValues((previous) => ({ ...previous, [field]: value }));
    setErrors((previous) => {
      const next = { ...previous };
      delete next[field as string];
      return next;
    });
  };

  const handleExtract = () => {
    if (!file) {
      return;
    }
    setExtracting(true);
    const formData = new FormData();
    formData.append('file', file);

    extractW9(
      { vendor_id: vendorId, branch_id: branchId, formData },
      (message: string, response: any) => {
        setExtracting(false);
        const data = response?.data?.data ?? {};
        setFileId(data.file_id ?? null);
        setWarnings(data.warnings ?? []);
        setValues(toReview(data.extracted ?? {}));
        setStep('review');
      },
      (message: string) => {
        setExtracting(false);
        // eslint-disable-next-line no-alert
        alert(message || t('w9.errors.extract_failed'));
      },
    );
  };

  const validate = (): boolean => {
    const found: Record<string, string> = {};
    if (!values.legal_name.trim()) {
      found.legal_name = t('w9.errors.legal_name_required');
    }
    if (values.tin.replace(/\D/g, '').length !== 9) {
      found.tin = t('w9.errors.tin_required');
    }
    if (values.entity_type === LLC_ENTITY_TYPE && !values.llc_tax_class) {
      found.llc_tax_class = t('w9.errors.llc_class_required');
    }
    setErrors(found);
    return Object.keys(found).length === 0;
  };

  const handleEnterManually = () => {
    setFileId(null);
    setWarnings([]);
    setValues({
      ...EMPTY_REVIEW,
      legal_name: vendorName || '',
      signature_date: new Date().toISOString().split('T')[0],
    });
    setStep('review');
  };

  const handleDownloadPdf = () => {
    if (!downloadW9Pdf) {
      return;
    }
    setDownloadingPdf(true);
    downloadW9Pdf(
      { vendor_id: vendorId, branch_id: branchId },
      (_message: string, blob: Blob) => {
        setDownloadingPdf(false);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      },
      (err: any) => {
        setDownloadingPdf(false);
        toast.error(err?.response?.data?.message || t('w9.errors.no_pdf', 'No W-9 PDF available to download'));
      },
    );
  };

  const handleConfirm = () => {
    if (!validate()) {
      return;
    }
    setConfirming(true);

    const payload: Record<string, any> = {
      vendor_id: vendorId,
      branch_id: branchId,
      file_id: fileId ?? null,
      legal_name: values.legal_name.trim(),
      entity_type: Number(values.entity_type),
      tin_type: Number(values.tin_type),
      tin: values.tin,
      has_foreign_partners: values.has_foreign_partners,
    };

    if (values.entity_type === LLC_ENTITY_TYPE && values.llc_tax_class) {
      payload.llc_tax_class = values.llc_tax_class;
    }

    const optionalText: Array<keyof ReviewValues> = [
      'business_name_dba', 'address_line1', 'address_line2', 'city', 'state',
      'postal_code', 'account_number', 'exempt_payee_code', 'fatca_code',
      'signer_name', 'signer_title', 'w9_revision',
    ];
    optionalText.forEach((field) => {
      const value = String(values[field] ?? '').trim();
      if (value) {
        payload[field] = value;
      }
    });
    if (values.signature_date) {
      payload.signature_date = values.signature_date;
    }

    confirmW9(
      payload,
      (message: string) => {
        setConfirming(false);
        onConfirmed();
        handleClose();
      },
      (message: string) => {
        setConfirming(false);
        toast.error(message || t('w9.errors.confirm_failed'));
      },
    );
  };

  return (
    <Modal show={show} onHide={handleClose} backdrop="static" centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{t('w9.title', { vendor: vendorName })}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {step === 'upload' && (
          <>
            {currentW9 && (
              <div className="alert alert-success d-flex justify-content-between align-items-center mb-3">
                <div>
                  <i className="fa fa-check-circle me-2" />
                  <strong>{t('w9.on_file_title', 'Current W-9 on file:')}</strong>{' '}
                  {currentW9.legal_name || vendorName}
                  {currentW9.masked_tin && (
                    <span className="badge bg-light text-dark ms-2">
                      TIN: {currentW9.masked_tin}
                    </span>
                  )}
                </div>
                {Boolean(currentW9.file_id) && downloadW9Pdf && (
                  <Button
                    variant="outline-success"
                    size="sm"
                    disabled={downloadingPdf}
                    onClick={handleDownloadPdf}
                  >
                    <i className="fa fa-file-pdf-o me-1" />
                    {downloadingPdf ? t('w9.downloading', 'Opening PDF...') : t('w9.view_pdf', 'View Signed W-9 PDF')}
                  </Button>
                )}
              </div>
            )}
            <p className="text-muted">{t('w9.upload_help')}</p>
            <Form.Group className="mb-3">
              <Form.Control
                type="file"
                accept="application/pdf"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setFile(event.target.files?.[0] ?? null)
                }
              />
            </Form.Group>

            <div className="text-center my-3 position-relative">
              <hr />
              <span
                className="position-absolute top-50 start-50 translate-middle px-3"
                style={{ backgroundColor: '#fff', color: '#6c757d', fontSize: '0.85rem' }}
              >
                OR
              </span>
            </div>

            <div className="text-center">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleEnterManually}
              >
                <i className="fa fa-pencil me-2" />
                {t('w9.enter_manually', 'Enter W-9 Details Manually')}
              </Button>
              <div className="form-text text-muted mt-1">
                {t('w9.manual_help', 'Type TIN, business structure, and address directly without uploading a signed PDF form.')}
              </div>
            </div>
          </>
        )}

        {step === 'review' && (
          <>
            <div className="alert alert-info py-2">{t('w9.review_help')}</div>
            {!fileId && (
              <div className="alert alert-secondary py-2">
                <i className="fa fa-pencil me-2" />
                <strong>{t('w9.manual_mode_title', 'Manual Entry Mode:')}</strong> {t('w9.manual_mode_body', 'Entering W-9 information manually without an uploaded PDF document.')}
              </div>
            )}
            {warnings.includes('tin_needs_manual_entry') && (
              <div className="alert alert-warning py-2">{t('w9.warnings.tin_needs_manual_entry')}</div>
            )}

            <div className="row">
              <Form.Group className="col-md-6 mb-3">
                <Form.Label>
                  {t('w9.legal_name')} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={values.legal_name}
                  isInvalid={Boolean(errors.legal_name)}
                  onChange={(event) => setField('legal_name', event.target.value)}
                />
                <Form.Control.Feedback type="invalid">{errors.legal_name}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="col-md-6 mb-3">
                <Form.Label>{t('w9.dba_name')}</Form.Label>
                <Form.Control
                  type="text"
                  value={values.business_name_dba}
                  onChange={(event) => setField('business_name_dba', event.target.value)}
                />
              </Form.Group>

              <Form.Group className="col-md-4 mb-3">
                <Form.Label>{t('w9.entity_type')}</Form.Label>
                <Form.Select
                  value={values.entity_type}
                  onChange={(event) => setField('entity_type', event.target.value)}
                >
                  {ENTITY_TYPES.map((option) => (
                    <option key={option.value} value={option.value}>{t(option.key)}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              {values.entity_type === LLC_ENTITY_TYPE && (
                <Form.Group className="col-md-4 mb-3">
                  <Form.Label>
                    {t('w9.llc_tax_class')} <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    value={values.llc_tax_class}
                    isInvalid={Boolean(errors.llc_tax_class)}
                    onChange={(event) => setField('llc_tax_class', event.target.value)}
                  >
                    <option value="">{t('w9.not_set')}</option>
                    {LLC_CLASSES.map((code) => (
                      <option key={code} value={code}>{code}</option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">{errors.llc_tax_class}</Form.Control.Feedback>
                </Form.Group>
              )}

              <Form.Group className="col-md-4 mb-3">
                <Form.Label>{t('w9.tin_type')}</Form.Label>
                <Form.Select
                  value={values.tin_type}
                  onChange={(event) => setField('tin_type', event.target.value)}
                >
                  <option value="1">{t('w9.tin_type_ein')}</option>
                  <option value="2">{t('w9.tin_type_ssn')}</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="col-md-4 mb-3">
                <Form.Label>
                  {t('w9.tin')} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  maxLength={11}
                  value={values.tin}
                  isInvalid={Boolean(errors.tin)}
                  onChange={(event) => setField('tin', event.target.value)}
                />
                <Form.Control.Feedback type="invalid">{errors.tin}</Form.Control.Feedback>
              </Form.Group>

              <div className="col-12"><hr /></div>

              <Form.Group className="col-md-6 mb-3">
                <Form.Label>{t('w9.address_line1')}</Form.Label>
                <Form.Control
                  type="text"
                  value={values.address_line1}
                  onChange={(event) => setField('address_line1', event.target.value)}
                />
              </Form.Group>
              <Form.Group className="col-md-6 mb-3">
                <Form.Label>{t('w9.address_line2')}</Form.Label>
                <Form.Control
                  type="text"
                  value={values.address_line2}
                  onChange={(event) => setField('address_line2', event.target.value)}
                />
              </Form.Group>
              <Form.Group className="col-md-4 mb-3">
                <Form.Label>{t('w9.city')}</Form.Label>
                <Form.Control
                  type="text"
                  value={values.city}
                  onChange={(event) => setField('city', event.target.value)}
                />
              </Form.Group>
              <Form.Group className="col-md-4 mb-3">
                <Form.Label>{t('w9.state')}</Form.Label>
                <Form.Control
                  type="text"
                  value={values.state}
                  onChange={(event) => setField('state', event.target.value)}
                />
              </Form.Group>
              <Form.Group className="col-md-4 mb-3">
                <Form.Label>{t('w9.postal_code')}</Form.Label>
                <Form.Control
                  type="text"
                  value={values.postal_code}
                  onChange={(event) => setField('postal_code', event.target.value)}
                />
              </Form.Group>

              <div className="col-12"><hr /></div>

              <Form.Group className="col-md-4 mb-3">
                <Form.Label>{t('w9.account_number')}</Form.Label>
                <Form.Control
                  type="text"
                  value={values.account_number}
                  onChange={(event) => setField('account_number', event.target.value)}
                />
              </Form.Group>
              <Form.Group className="col-md-4 mb-3">
                <Form.Label>{t('w9.exempt_payee_code')}</Form.Label>
                <Form.Control
                  type="text"
                  value={values.exempt_payee_code}
                  onChange={(event) => setField('exempt_payee_code', event.target.value)}
                />
              </Form.Group>
              <Form.Group className="col-md-4 mb-3">
                <Form.Label>{t('w9.fatca_code')}</Form.Label>
                <Form.Control
                  type="text"
                  value={values.fatca_code}
                  onChange={(event) => setField('fatca_code', event.target.value)}
                />
              </Form.Group>

              <Form.Group className="col-md-6 mb-3 d-flex align-items-center">
                <Form.Check
                  type="checkbox"
                  id="has_foreign_partners"
                  label={t('w9.has_foreign_partners')}
                  checked={values.has_foreign_partners}
                  onChange={(event) => setField('has_foreign_partners', event.target.checked)}
                />
              </Form.Group>

              <div className="col-12"><hr /></div>

              <Form.Group className="col-md-4 mb-3">
                <Form.Label>{t('w9.signer_name')}</Form.Label>
                <Form.Control
                  type="text"
                  value={values.signer_name}
                  onChange={(event) => setField('signer_name', event.target.value)}
                />
              </Form.Group>
              <Form.Group className="col-md-4 mb-3">
                <Form.Label>{t('w9.signer_title')}</Form.Label>
                <Form.Control
                  type="text"
                  value={values.signer_title}
                  onChange={(event) => setField('signer_title', event.target.value)}
                />
              </Form.Group>
              <Form.Group className="col-md-4 mb-3">
                <Form.Label>{t('w9.signature_date')}</Form.Label>
                <Form.Control
                  type="date"
                  value={values.signature_date}
                  onChange={(event) => setField('signature_date', event.target.value)}
                />
              </Form.Group>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {step === 'review' && (
          <Button variant="outline-secondary" onClick={() => setStep('upload')} className="me-auto">
            {t('w9.back', 'Back')}
          </Button>
        )}
        <Button variant="secondary" onClick={handleClose}>
          {t('w9.cancel')}
        </Button>
        {step === 'upload' && (
          <Button variant="primary" onClick={handleExtract} disabled={!file || extracting}>
            {extracting ? t('w9.reading') : t('w9.read_form')}
          </Button>
        )}
        {step === 'review' && (
          <Button variant="primary" onClick={handleConfirm} disabled={confirming}>
            {confirming ? t('w9.confirming') : t('w9.confirm')}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default W9Modal;
